import type { FC } from 'react'
import { useMemo } from 'react'

import type { SxProps, Theme } from '@mui/material'
import { Box, Link, ScopedCssBaseline, Typography } from '@mui/material'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { Link as RouterLink } from 'react-router-dom'

import { getThemeByMode } from '@/common/theme'

import {
  ConversationDemoSection,
  ConversationFeaturesSection,
  DraftsPersistenceSection,
  EventDetailDemoSection,
  EventInspectorDemoSection,
  EventTilesSection,
  FeatureHighlightsSection,
  GridBackground,
  HeroSection,
  LiveActivityDemoSection,
  NotificationsSection,
  PromisesSection,
  QuickStartPathSection,
  SessionModesSection,
  ShowcaseSection,
  StickyCtaButton,
  TerminalTypewriter,
  UserInputDemoSection,
} from '../components'
import { HOME_CONTENT } from '../defaults'

export const HomePage: FC = () => {
  const darkTheme = useMemo(() => getThemeByMode('dark'), [])

  return (
    <MuiThemeProvider theme={darkTheme}>
      <ScopedCssBaseline>
        <Box component="main" sx={pageContainerSx}>
          <GridBackground />
          <StickyCtaButton />

          <HeroSection />

          <TerminalTypewriter />

          <PromisesSection />

          <QuickStartPathSection />

          <ShowcaseSection />

          <EventTilesSection />

          <LiveActivityDemoSection />

          <SessionModesSection />

          <FeatureHighlightsSection />

          <NotificationsSection />

          <ConversationDemoSection />

          <ConversationFeaturesSection />

          <EventDetailDemoSection />

          <UserInputDemoSection />

          <DraftsPersistenceSection />

          <EventInspectorDemoSection />

          <Box component="footer" sx={footerSx}>
            <Typography variant="body2" sx={footerTextSx}>
              <Link component={RouterLink} to="/docs" sx={docsLinkSx}>
                {HOME_CONTENT.docsLink}
              </Link>
            </Typography>
          </Box>
        </Box>
      </ScopedCssBaseline>
    </MuiThemeProvider>
  )
}

const pageContainerSx: SxProps<Theme> = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
}

const footerSx: SxProps<Theme> = {
  mt: 'auto',
  py: 4,
  textAlign: 'center',
  borderTop: 1,
  borderColor: 'divider',
}

const footerTextSx: SxProps<Theme> = {
  color: 'text.secondary',
}

const docsLinkSx: SxProps<Theme> = {
  color: 'primary.main',
  textDecoration: 'none',
  fontWeight: 500,
  '&:hover': {
    textDecoration: 'underline',
  },
}
